import type { EmbeddedSpeechifyExperience } from "https://storage.googleapis.com/speechify-api-cdn/speechifyapi.min.mjs";
import { atomWithReducer } from "jotai/utils";
import type { YouTubePlayer } from "youtube-player/dist/types";

type OnlyOnePlayingPlayer = {
  adsManager: OrUndefined<google.ima.AdsManager>;
  articleSpeechifyPlayer: OrUndefined<EmbeddedSpeechifyExperience>;
  isAdManualPaused: boolean;
  isAdPlaying: boolean;
  isAdStarted: boolean;
  isArticleSpeechifyPlayerPlaying: boolean;
  playingYoutubeId: OrUndefined<string>;
  playingYoutubePlayer: OrUndefined<YouTubePlayer>;
  shouldPauseAd: boolean;
  shouldPauseArticleSpeechifyPlayer: boolean;
  shouldPauseYouTubePlayer: boolean;
};

type OnlyOnePlayingPlayerAction =
  | {
      payload: {
        adsManager: google.ima.AdsManager;
      };
      type: "loadAdManager";
    }
  | {
      payload: {
        articleSpeechifyPlayer: EmbeddedSpeechifyExperience;
      };
      type: "loadArticleSpeechPlayer";
    }
  | {
      payload: {
        isIntersecting: OrUndefined<boolean>;
      };
      type: "pauseAd";
    }
  | {
      type: "pauseArticleSpeechifyPlayer";
    }
  | {
      type: "pauseYouTuberPlayer";
    }
  | {
      type: "playArticleSpeechifyPlayer";
    }
  | {
      payload: {
        playingYoutubeId: string;
        playingYoutubePlayer: OrUndefined<YouTubePlayer>;
      };
      type: "playYouTuberPlayer";
    }
  | {
      payload: {
        isIntersecting: OrUndefined<boolean>;
      };
      type: "resumeAd";
    }
  | {
      type: "skipAd";
    }
  | {
      payload: {
        isIntersecting: OrUndefined<boolean>;
      };
      type: "startAd";
    };

const onlyOnePlayingPlayerReducer = (
  previous: OnlyOnePlayingPlayer,
  action: OnlyOnePlayingPlayerAction,
) => {
  switch (action.type) {
    case "loadArticleSpeechPlayer":
      return {
        ...previous,
        articleSpeechifyPlayer: action.payload.articleSpeechifyPlayer,
      };
    case "loadAdManager":
      return {
        ...previous,
        adsManager: action.payload.adsManager,
      };
    case "pauseAd":
      return {
        ...previous,
        isAdManualPaused:
          action.payload.isIntersecting === true && !previous.isArticleSpeechifyPlayerPlaying,
        isAdPlaying: false,
        shouldPauseAd: false,
      };
    case "pauseArticleSpeechifyPlayer":
      return {
        ...previous,
        isArticleSpeechifyPlayerPlaying: false,
        shouldPauseArticleSpeechifyPlayer: false,
      };
    case "pauseYouTuberPlayer":
      return {
        ...previous,
        playingYoutubeId: undefined,
        playingYoutubePlayer: undefined,
        shouldPauseYouTubePlayer: false,
      };
    case "playArticleSpeechifyPlayer":
      return {
        ...previous,
        isArticleSpeechifyPlayerPlaying: true,
        shouldPauseAd: previous.isAdPlaying,
        shouldPauseYouTubePlayer: !previous.isAdStarted && !!previous.playingYoutubeId,
      };
    case "playYouTuberPlayer":
      return {
        ...previous,
        playingYoutubeId: action.payload.playingYoutubeId,
        playingYoutubePlayer: action.payload.playingYoutubePlayer,
        shouldPauseArticleSpeechifyPlayer: previous.isArticleSpeechifyPlayerPlaying,
      };
    case "resumeAd":
      return {
        ...previous,
        isAdManualPaused: false,
        isAdPlaying: true,
        shouldPauseArticleSpeechifyPlayer:
          action.payload.isIntersecting === true && previous.isArticleSpeechifyPlayerPlaying,
      };
    case "skipAd":
      return {
        ...previous,
        isAdPlaying: false,
        isAdStarted: false,
        shouldPauseAd: false,
      };
    case "startAd":
      return {
        ...previous,
        isAdPlaying: true,
        isAdStarted: true,
        shouldPauseArticleSpeechifyPlayer:
          action.payload.isIntersecting === true && previous.isArticleSpeechifyPlayerPlaying,
      };
    default:
      throw new Error("unknown only one playing player action type");
  }
};

const defaultOnlyOnePlayingPlayerAtom: OnlyOnePlayingPlayer = {
  adsManager: undefined,
  articleSpeechifyPlayer: undefined,
  isAdManualPaused: false,
  isAdPlaying: false,
  isAdStarted: false,
  isArticleSpeechifyPlayerPlaying: false,
  playingYoutubeId: undefined,
  playingYoutubePlayer: undefined,
  shouldPauseAd: false,
  shouldPauseArticleSpeechifyPlayer: false,
  shouldPauseYouTubePlayer: false,
};

export const onlyOnePlayingPlayerAtom = atomWithReducer(
  defaultOnlyOnePlayingPlayerAtom,
  onlyOnePlayingPlayerReducer,
);
